/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'

import moment from 'moment'
import { compose } from 'ramda'
import { createEditor } from 'slate'
import { useTranslation } from 'react-i18next'
import { Editable, Slate, withReact } from 'slate-react'
import { COLORS } from '@btime/constants'

import { Absolute, Relative } from '../../../kiti/components/helpers/Position.js'
import { deserializeText, serializeText } from '../_serializers/textSerializer.js'
import { renderElement, renderLeaf } from '../_plugins/plugins.js'
import { useEditorKeydown } from '../_plugins/keydowns.js'
import EditorContext from '../EditorContext.js'
import InterpolationPlugin from '../_plugins/InterpolationPlugin.js'
import Panel from '../../../kiti/components/structure/Panel.js'
import Separator from '../../../kiti/components/helpers/Separator.js'
import Button from '../../../kiti/components/actions/Button.js'
import Modal from '../../../../components/structure/Modal.js'
import TimeInput from '../../../../components/form/TimeInput.js'
import FlexGrid from '../../../../components/structure/FlexGrid.js'
import DayPicker from '../../../../components/form/date/DayPicker.js'
import InputButton from '../../../../components/form/InputButton.js'
import { INTERPOLATION_REGEX, isInterpolation } from '../../../../utils/helpers/interpolation.js'
import { isDateTime } from '../../../../utils/helpers/date.js'
import UniqueInterpolationPlugin from '../_plugins/UniqueInterpolationPlugin.js'

const DateInputWithInterpolations = ({ value, onChange, interpolations, disabled, useTime, ...props }) => {
  const styles = css`
    min-height: 42.28px;
    & .panel {
      border-color: ${COLORS.light5};
      border-radius: 0.3rem;
    }
    & .inputButtonWrapper .input {
      height: 42.78px !important;
      background-color: #ffffff !important;
    }
    & .text {
      line-height: 1.5;
    }
  `

  const [editor] = React.useState(() =>
    compose(InterpolationPlugin.extend({ fullControl: false }), withReact)(createEditor())
  )
  editor.singleLine = true
  editor.singleItem = true

  const availableTypes = useTime ? ['raw_datetime'] : ['raw_date', 'raw_datetime']
  const onlyDatesInterpolations = interpolations.filter(interpolation => availableTypes.includes(interpolation?.type))

  const initialValue = deserializeText(value || '', onlyDatesInterpolations)
  const initialDateTimeValue = moment(value).isValid() ? value : undefined

  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const onClose = () => setOpen(false)
  const onOpen = () => setOpen(true)

  const [resetKey, setResetKey] = useState(0)

  const [time, setTime] = useState(initialDateTimeValue)
  const [day, setDay] = useState(initialDateTimeValue)

  const formatStr = 'DD/MM/YYYY' + (useTime ? ' HH:mm' : '')

  const changeAndClose = value => {
    if (!value) {
      setDay(undefined)
      onChange(undefined)
      onClose()
      return
    }
    onChange(moment(value).format('YYYY-MM-DD'))
    onClose()
  }

  const handleChangeDay = useTime ? setDay : changeAndClose
  const handleChangeTime = setTime

  const onConfirmDateTime = () => {
    if (!day) {
      onChange(undefined)
    } else {
      const dateTime = moment(day).hour(moment(time).hour()).minute(moment(time).minute()).toDate()
      onChange(moment.utc(dateTime).format())
    }
    onClose()
  }

  const handleSetValue = value => {
    const serializedValue = serializeText(value)
    const cleanValue = isInterpolation(serializedValue)
      ? serializedValue.match(INTERPOLATION_REGEX)?.[0]
      : serializedValue
    if (isDateTime(serializedValue)) {
      setResetKey(resetKey => resetKey + 1)
    }
    onChange(cleanValue)
  }

  const hasInterpolation = isInterpolation(value) || value === null
  const formattedValue = value && moment(value).format(formatStr)

  return (
    <div css={styles}>
      <Relative zIndex="auto">
        <Panel padding={0} radius={0.4}>
          <Slate editor={editor} value={initialValue} onChange={handleSetValue} key={resetKey}>
            <EditorContext size="small">
              <Relative zIndex="auto">
                <Separator className="inputWrapper" paddingV={0.6} paddingH={0.7}>
                  <Editable
                    onKeyDown={useEditorKeydown(editor)}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                  />
                  <Absolute right={0} top={-1.5} zIndex="auto">
                    <UniqueInterpolationPlugin.Dropdown options={onlyDatesInterpolations} />
                  </Absolute>
                </Separator>
              </Relative>
            </EditorContext>
          </Slate>
        </Panel>

        {!hasInterpolation && (
          <Absolute left={0} top={0} zIndex="auto" width="100%">
            <div className="inputButtonWrapper">
              <InputButton
                paddingV={9.6}
                disabled={disabled}
                onClick={onOpen}
                value={formattedValue}
                iconRight="BsCalendar"
              />
            </div>
          </Absolute>
        )}
      </Relative>
      <Modal open={open} onClose={onClose} width="22rem" minHeight="28rem">
        <DayPicker value={day} onChange={handleChangeDay} {...props} />
        {useTime && (
          <div>
            <Separator weight={4} />
            <TimeInput value={time} onChange={handleChangeTime} disabled={!day} />
            <Separator weight={6} />
            <FlexGrid justify="flex-end">
              <Button onClick={onConfirmDateTime}>{t('confirm')}</Button>
            </FlexGrid>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default DateInputWithInterpolations
